import {
    IAfterGuiAttachedParams,
    IDoesFilterPassParams,
    IFilterComp,
    IFilterParams,
    IFloatingFilterComp,
    IFloatingFilterParams,
} from '@ag-grid-community/core';
import { FilterOption } from '@/components/ui/responsive-grid/types';

export interface StatusFilterParams extends IFilterParams {
    intakeDashboardEnabled: boolean;
}

// Display labels for the floating filter
export const STATUS_LABELS: Record<string, string> = {
    '': 'All',
    'referral intake pending admitted': 'Active',
    referral: 'Referral',
    intake: 'Intake',
    pending: 'Pending',
    admitted: 'Admitted',
    transferred: 'Transferred',
    discharged: 'Discharged',
    'non admit': 'Non Admit',
};

export class StatusFilter implements IFilterComp {
    filterParams!: StatusFilterParams;
    filterText!: string | null;
    gui!: HTMLDivElement;
    eFilterText: HTMLSelectElement | null = null;

    static getFilterOptions(params?: { intakeDashboardEnabled?: boolean }): FilterOption[] {
        return Object.entries(STATUS_LABELS)
            .filter(([value]) => value !== 'referral' || (params?.intakeDashboardEnabled ?? true))
            .map(([value, label]) => ({ value, label }));
    }

    init(params: StatusFilterParams) {
        this.filterParams = params;
        this.filterText = null;
        this.setupGui(params);
    }

    setupGui(params: StatusFilterParams) {
        this.gui = document.createElement('div');

        const label = document.createElement('label');
        label.setAttribute('for', 'filterText');

        const select = document.createElement('select');
        select.id = 'filterText';

        for (const opt of StatusFilter.getFilterOptions({ intakeDashboardEnabled: params.intakeDashboardEnabled })) {
            const option = document.createElement('option');
            option.value = opt.value;
            option.textContent = opt.label;
            select.appendChild(option);
        }

        label.appendChild(select);

        const wrapper = document.createElement('div');
        wrapper.className = 'ag-person-filter';
        wrapper.appendChild(label);

        this.gui.appendChild(wrapper);

        const listener = (event: Event) => {
            const target = event.target as HTMLSelectElement;
            this.filterText = target.value;
            params.filterChangedCallback();
        };

        this.eFilterText = this.gui.querySelector('#filterText');
        this.eFilterText?.addEventListener('change', listener);
        this.eFilterText?.addEventListener('paste', listener);
        this.eFilterText?.addEventListener('input', listener);
    }

    getGui() {
        return this.gui;
    }

    doesFilterPass(params: IDoesFilterPassParams) {
        const { node } = params;
        const entries = this.filterParams.getValue(node)?.toString().toLowerCase();

        if (!entries) {
            return false;
        }

        if (!this.filterText) {
            return true;
        }

        const filterWords = this.filterText.toLowerCase().split(' ');

        return filterWords.some((word) => entries.includes(word));
    }

    isFilterActive() {
        return this.filterText != null && this.filterText !== '';
    }

    getModel() {
        if (!this.isFilterActive()) {
            return null;
        }

        return { value: this.filterText };
    }

    setModel(model: { value: string } | null) {
        const newValue = model?.value || '';
        if (this.eFilterText) {
            this.eFilterText.value = newValue;
        }
        this.filterText = newValue;
    }

    afterGuiAttached(params?: IAfterGuiAttachedParams): void {
        if (!params?.suppressFocus) {
            // focus the input element for keyboard navigation
            this.eFilterText?.focus();
        }
    }
}

export class StatusFloatingFilter implements IFloatingFilterComp {
    private eGui!: HTMLDivElement;
    private eSelect!: HTMLSelectElement;
    private params!: IFloatingFilterParams<StatusFilter>;

    init(params: IFloatingFilterParams<StatusFilter>) {
        this.params = params;
        this.eGui = document.createElement('div');
        this.eGui.style.width = '100%';
        this.eGui.style.height = '100%';
        this.eGui.style.display = 'flex';
        this.eGui.style.alignItems = 'center';

        this.eSelect = document.createElement('select');
        this.eSelect.style.width = '100%';
        this.eSelect.style.height = '24px';
        this.eSelect.style.fontSize = '12px';
        this.eSelect.className = 'ag-input-field-input ag-text-field-input';

        // Add options
        const filterParams = params.filterParams as StatusFilterParams | undefined;
        for (const opt of StatusFilter.getFilterOptions({ intakeDashboardEnabled: filterParams?.intakeDashboardEnabled })) {
            const option = document.createElement('option');
            option.value = opt.value;
            option.textContent = opt.label;
            this.eSelect.appendChild(option);
        }

        this.eSelect.addEventListener('change', () => {
            this.params.parentFilterInstance((instance) => {
                instance.setModel(this.eSelect.value ? { value: this.eSelect.value } : null);
                instance.filterParams.filterChangedCallback();
            });
        });

        this.eGui.appendChild(this.eSelect);
    }

    getGui() {
        return this.eGui;
    }

    onParentModelChanged(parentModel: { value: string } | null) {
        this.eSelect.value = parentModel?.value ?? '';
    }
}
